import {
  Box,
  Divider,
  useBreakpointValue,
  useEventListener,
} from "@chakra-ui/react";
import useTranslate from "hooks/useTranslate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SwiperSlide } from "swiper/react/swiper-react";
import { Controller } from "swiper";
import Tab from "components/Tab";
import { immutableReplaceAt } from "utils/arrayUtils";
import useDressingRoom from "hooks/useDressingRoom";
import EmptyState from "components/EmptyState";
import ControlledSwiper from "components/ControlledSwiper";
import useV2 from "hooks/useV2";
import { getCategoryIndexByName } from "utils/traitsUtils";
import { swapableTraitCategories } from "config/categories";

type TraitsSelectorContainerProps = {
  renderItem: (
    tokenId: number,
    isSelected: boolean,
    isOriginal: boolean,
    isVisible: boolean
  ) => JSX.Element;
};

const TraitsSelectorContainer = ({
  renderItem,
}: TraitsSelectorContainerProps) => {
  const translate = useTranslate();
  const { isLoading } = useV2();
  const {
    originalTraits,
    currentTraits,
    availableTraitsByCategory,
    swapTrait,
  } = useDressingRoom();

  const availableCategories = useMemo(
    () =>
      swapableTraitCategories.filter((category) => {
        const traitsByCategory = availableTraitsByCategory?.[category];
        return traitsByCategory && traitsByCategory.length > 0;
      }),
    [availableTraitsByCategory]
  );

  const [currentCategory, setCurrentCategory] = useState(
    availableCategories[0]
  );

  const traitsInCurrentCategory = useMemo(
    () =>
      availableTraitsByCategory?.[currentCategory]?.sort((a, b) => b - a) || [],
    [availableTraitsByCategory, currentCategory]
  );

  const currentTraitIndex = useMemo(
    () =>
      traitsInCurrentCategory?.findIndex(
        (tokenId) => currentTraits.indexOf(tokenId) >= 0
      ),
    [traitsInCurrentCategory, currentTraits]
  );

  const currentCategoryIndex = availableCategories.indexOf(currentCategory);

  const slidesPerView = useBreakpointValue({
    base: 3.25,
    sm: 4.4,
  });
  const itemsGap = useBreakpointValue({
    base: 5,
    sm: 4,
  });

  const [tabsSwiper, setTabsSwiper] = useState<any>(null);
  const [carouselSwiper, setCarouselSwiper] = useState<any>(null);

  const handleCategoryChange = useCallback(
    (category: string) => {
      setCurrentCategory(category);
    },
    [setCurrentCategory]
  );

  const handleSelect = useCallback(
    (tokenId: number) => {
      if (currentTraits.includes(tokenId)) {
        return;
      }

      const categoryIndex = getCategoryIndexByName(currentCategory);
      swapTrait(
        immutableReplaceAt<number>(currentTraits, categoryIndex, tokenId)
      );
    },
    [currentTraits, currentCategory, swapTrait]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (currentTraitIndex === undefined || !traitsInCurrentCategory) {
        return;
      }

      let index: number = -1;
      if (
        event.key === "ArrowRight" &&
        currentTraitIndex < traitsInCurrentCategory.length - 1
      ) {
        index = currentTraitIndex + 1;
      }
      if (event.key === "ArrowLeft" && currentTraitIndex > 0) {
        index = currentTraitIndex - 1;
      }

      if (index >= 0) {
        handleSelect(traitsInCurrentCategory[index]);
      }
    },
    [currentTraitIndex, traitsInCurrentCategory, handleSelect]
  );

  useEffect(() => {
    if (!carouselSwiper || carouselSwiper.destroyed || isLoading) {
      return;
    }
    if (currentTraitIndex >= 0) {
      carouselSwiper.slideTo(Math.max(currentTraitIndex - 1, 0));
    }
  }, [currentTraitIndex, carouselSwiper, traitsInCurrentCategory, isLoading]);

  useEffect(() => {
    if (!tabsSwiper || tabsSwiper.destroyed) {
      return;
    }
    if (currentCategoryIndex >= 0) {
      tabsSwiper.slideTo(Math.max(currentCategoryIndex - 1, 0));
    }
  }, [currentCategoryIndex, tabsSwiper]);

  useEventListener("keydown", handleKeyPress);

  return (
    <Box w="full">
      {availableCategories?.length ? (
        <>
          <Box position="relative">
            <Divider position="absolute" bottom="0" left="0" right="0" />

            <ControlledSwiper
              spaceBetween={0}
              slidesPerView="auto"
              threshold={10}
              modules={[Controller]}
              onSwiper={setTabsSwiper}
              freeMode
            >
              {availableCategories.map((category) => (
                <SwiperSlide
                  key={category}
                  style={{
                    width: "auto",
                  }}
                >
                  <Tab
                    size="sm"
                    px={{
                      base: "3",
                      md: "4",
                    }}
                    isActive={currentCategory === category}
                    onClick={() => handleCategoryChange(category)}
                  >
                    {translate(`traitCategories:${category}`)}
                  </Tab>
                </SwiperSlide>
              ))}
            </ControlledSwiper>
          </Box>

          <Box mt="3">
            {traitsInCurrentCategory?.length ? (
              <ControlledSwiper
                spaceBetween={itemsGap}
                threshold={10}
                slidesPerView={slidesPerView}
                modules={[Controller]}
                onSwiper={setCarouselSwiper}
                freeMode
                watchSlidesProgress
              >
                {traitsInCurrentCategory.map((tokenId) => (
                  <SwiperSlide key={tokenId}>
                    {({ isVisible }) => (
                      <Box
                        role="button"
                        tabIndex={0}
                        cursor="pointer"
                        onClick={() => handleSelect(tokenId)}
                      >
                        {renderItem(
                          tokenId,
                          currentTraits.includes(tokenId),
                          originalTraits.includes(tokenId),
                          isVisible
                        )}
                      </Box>
                    )}
                  </SwiperSlide>
                ))}
              </ControlledSwiper>
            ) : (
              <EmptyState py="7" opacity="0.4" />
            )}
          </Box>
        </>
      ) : (
        <EmptyState py="7" opacity="0.4" />
      )}
    </Box>
  );
};

export default TraitsSelectorContainer;
